<template>
  <div class="layout-container">
    <CRNavigation
      v-append-parameters="urlParams"
      :branding-global="jobShop?.branding"
      :navigation-global="jobShop?.navigation"
      :social-media-global="jobShop?.['social-media']"
      :page-locale="jobShop?.locale"
      :job-shop-url="jobShop?.jobShopUrl"
      :skip-link-label="$t('navigation.skipToMainContent')"
      :menu-toggle-label="$t('navigation.toggleMenu')"
      :main-nav-label="$t('navigation.mainNav')"
      :mobile-nav-label="$t('navigation.mobileNav')"
      :logo-alt-label="$t('navigation.logoHome')"
      @emitted-click="
        (e) => {
          trackNavigationClicked(e);
        }
      "
    />

    <main id="cr-main-content" class="main-container">
      <slot />
    </main>

    <CRFooter
      v-append-parameters="urlParams"
      :social-media-global="jobShop?.['social-media']"
      :navigation-global="jobShop?.navigation"
      :branding-global="jobShop?.branding"
      :footer-global="jobShop?.footer"
      :job-shop-url="jobShop?.jobShopUrl"
      :footer-logo-alt-label="$t('navigation.footerLogoHome')"
      @cookie-settings-click="$cookieConsent.openCookieSettings()"
    />
  </div>
</template>

<script setup lang="ts">
import { useJobShopData } from "@/composables/useJobShopData"
import { useTypesenseApiKey } from "@/composables/useTypesenseApiKey"
import { useOrganizationData } from "@/composables/useOrganizationData"
import { buildOrganizationSchema } from "@/utils/organizationSchema"

const { $cookieConsent } = useNuxtApp()
const urlParams = useQueryHeaders()
// Capture i18n helpers before the first await (async-setup constraint).
const { setLocale, locales } = useI18n()

// Fetch JobShop data, the typesenseApiKey and Organization schema data in
// parallel. None depend on jobShopData (all read what they need from the header
// store), so they can overlap instead of running serially on the SSR path.
// The font SRI manifest is resolved here rather than inside useFonts: it reads the
// feature flag, which reads useNuxtApp()/useHeaderStore() and must therefore run before
// the first await (async-setup constraint). Building the promise here keeps that
// synchronous prologue in the intact instance context.
// useOrganizationData reuses the layout's key request instead of fetching its own.
const typesenseApiKeyResult = useTypesenseApiKey()
const [{ jobShop }, , { organizationData }, fontIntegrity] = await Promise.all([
  useJobShopData(),
  typesenseApiKeyResult,
  useOrganizationData(typesenseApiKeyResult.then(({ typesenseApiKey }) => typesenseApiKey.value)),
  useFontIntegrity(),
])

// Drive the whole UI from the jobshop's locale (not the visitor's browser language):
// resolve it to a registered i18n code ("de-DE" -> "de") and make it the active locale.
// setLocale loads that locale's messages, so every t() call (incl. crossroads internals)
// resolves in the jobshop language during SSR — no en-fallback warnings / wrong language.
await setLocale(resolveI18nLocale(jobShop.value?.locale, locales.value.map(l => l.code), "en"))

// Set fonts & font-weights
useFonts(jobShop?.value, fontIntegrity)

// set font size class
useHeadingFontClass(jobShop?.value)

// Server-side so Google's JS-less verification fetcher sees the token in initial HTML.
useSiteVerification(jobShop?.value)

// Use JobShop data to set the favicon with useHeadSafe
const brandingConfig = jobShop.value?.branding.config.general

useHeadSafe({
  htmlAttrs: {
    lang: jobShop.value?.locale,
  },
  link: [
    {
      rel: "icon",
      href: brandingConfig?.favicon.media.url,
    },
  ],
})

// Add the site-identity schema: WebSite plus Organization (built from the cached
// KPI profile + job cities). The Organization node is distinct (#organization)
// from the JobPosting page's nested hiringOrganization, so they coexist without
// clashing. (JET-1819)
if (jobShop.value) {
  useSchemaOrg([
    defineWebSite({
      "@id": `${jobShop.value.jobShopUrl}#website`,
      "name": jobShop.value.jobShopTitle,
      "url": jobShop.value.jobShopUrl,
      "inLanguage": jobShop.value.locale,
    }),
    defineOrganization(buildOrganizationSchema({
      jobShop: jobShop.value,
      profile: organizationData.value?.profile,
      cities: organizationData.value?.cities,
    })),
  ])
}
</script>

<style lang="scss" scoped>
.layout-container {
  /*
    CRHero "Flexible" layout uses calc(100dvh - offset) inside <main>; subtract sticky
    CRNavigation so hero content stays in the visible viewport (78px matches Crossroads
    laptop nav bar including -large).
  */
  --hero-dynamic-viewport-offset: 48px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  width: 100%;
  min-height: 100%;

  @media (min-width: $breakpoint-tablet) {
    --hero-dynamic-viewport-offset: 78px;
  }
}

.main-container {
  flex: 1;
}
</style>
